import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { format, isValid } from 'date-fns';
import cx from 'classnames';
import DatePicker, { registerLocale } from 'react-datepicker';
import pl from 'date-fns/locale/pl';

import { Button } from '../Button/Button';

export const RouteForm = ({
    formik,
    cars,
    route,
    ramps,
    drivers,
    planningMode,
    onPlanningModeChange,
    onEndPointChange,
    handleRecalculateSpeed,
    handleUnloadingTimeForAllChange,
    travelToFirstPointMs,
}) => {
    const [defaultUnloadingTime, setDefaultUnloadingTime] = useState(30);
    registerLocale('pl', pl);

    const toDateOrNull = (value) => {
        if (!value) return null;

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const carOptions = cars.map((car) => ({ value: car.id, label: car.name }));
    const driverOptions = Object.keys(drivers).map((driver) => ({ value: parseInt(driver), label: drivers[driver] }));
    const selectedCar = carOptions.find((option) => option.value === formik.values.carId);
    const selectedDriver = driverOptions.find((option) => option.value === formik.values.driverId);
    const isDepartureDisabled = planningMode === 'first_pickup';
    const isFirstPickupDisabled = planningMode === 'departure';
    const disabledDateStyle = { opacity: 0.5, pointerEvents: 'none' };

    const rsStyles = (isInvalid) => ({
        control: (base, state) => ({
            ...base,
            minHeight: 'calc(1.5em + .75rem + 2px)',
            borderRadius: '.25rem',
            borderColor: isInvalid ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: isInvalid
                ? '0 0 0 .2rem rgba(220,53,69,.25)'
                : state.isFocused
                    ? '0 0 0 .2rem rgba(13,110,253,.25)'
                    : 'none',
            backgroundColor: '#fff',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 .75rem',
        }),
        input: (base) => ({
            ...base,
            color: '#212529',
        }),
        singleValue: (base) => ({
            ...base,
            color: '#212529',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#475F7B',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            paddingRight: '.5rem',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '.375rem .5rem',
            color: '#6c757d',
        }),
    });

    const carInvalid = formik.touched.carId && formik.errors.carId;
    const driverInvalid = formik.touched.driverId && formik.errors.driverId;

    useEffect(() => {
        if (!formik.values.departureAt && formik.values.firstPickupAt) {
            formik.setFieldValue('firstPickupAt', '');
        }
    }, [formik.values.departureAt]);

    useEffect(() => {
        if (!formik.values.firstPickupAt && formik.values.departureAt && planningMode === 'first_pickup') {
            formik.setFieldValue('departureAt', '');
            formik.setFieldValue('routeDate', '');
            formik.setFieldValue('startTime', '');
        }
    }, [formik.values.firstPickupAt, planningMode]);

    return (
        <>
            {/* Row 1: Route name */}
            <div className="form-group row">
                <label htmlFor="name" className="col-lg-2 col-form-label">
                    {__('routes.route_name')}
                </label>
                <div className="col-lg-10">
                    <input
                        className={cx('form-control', {
                            'is-invalid': formik.errors.name && formik.touched.name,
                        })}
                        placeholder={__('routes.route_name_placeholder')}
                        id="name"
                        name="name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                    />
                </div>
            </div>

            {/* Row 2: DATE/TIME SECTION */}
            <div className="card shadow-none border mb-2">
                <div className="card-body p-2">
                    <h6 className="card-title mb-1">{__('routes.dates_section')}</h6>

                    <div className="form-group row mb-1">
                        <label htmlFor="startPoint" className="col-lg-2 col-form-label">
                            {__('routes.start_from')}
                        </label>
                        <div className="col-lg-4">
                            <div className="input-group">
                                <input
                                    className={cx('form-control', {
                                        'is-invalid': formik.errors.startPoint && formik.touched.startPoint,
                                    })}
                                    placeholder={__('routes.start_from_placeholder')}
                                    id="startPoint"
                                    name="startPoint"
                                    type="text"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.startPoint ?? ''}
                                />
                                <div className="input-group-append" id="button-addon2">
                                    <Button
                                        className="btn-primary waves-effect waves-light"
                                        onClick={() => formik.setFieldValue('startPoint', route.startPoint)}
                                        icon="bx bx-home"
                                    />
                                </div>
                            </div>
                        </div>

                        <label htmlFor="endPoint" className="col-lg-2 col-form-label">
                            {__('routes.route_end')}
                        </label>
                        <div className="col-lg-4">
                            <div className="input-group">
                                <input
                                    className="form-control"
                                    placeholder={__('routes.route_end_placeholder')}
                                    id="endPoint"
                                    name="endPoint"
                                    type="text"
                                    onChange={onEndPointChange}
                                    value={formik.values.endPoint ?? ''}
                                />
                                <div className="input-group-append" id="button-addon2">
                                    <Button
                                        className="btn-primary waves-effect waves-light"
                                        onClick={() => onEndPointChange({ target: { name: 'endPoint', value: route.endPoint ?? '' } })}
                                        icon="bx bx-home"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-group row mb-1">
                        <label htmlFor="loadingAt" className="col-lg-2 col-form-label">
                            {__('routes.loading_date_time')}
                        </label>
                        <div className="col-lg-10">
                            <DatePicker
                                className={cx('form-control', {
                                    'is-invalid': formik.values.loadingAt && formik.values.departureAt
                                        && toDateOrNull(formik.values.loadingAt) > toDateOrNull(formik.values.departureAt),
                                })}
                                wrapperClassName="col-12"
                                selected={toDateOrNull(formik.values.loadingAt)}
                                onChange={(date) => {
                                    if (date && !isValid(date)) return;
                                    formik.setFieldValue('loadingAt', date ? format(date, 'yyyy-MM-dd HH:mm') : '');
                                }}
                                locale="pl"
                                showTimeSelect
                                dateFormat="yyyy-MM-dd HH:mm"
                                placeholderText={__('routes.loading_date_time_placeholder')}
                                isClearable
                            />
                            {formik.values.loadingAt && formik.values.departureAt
                                && toDateOrNull(formik.values.loadingAt) > toDateOrNull(formik.values.departureAt) && (
                                <div className="invalid-feedback d-block">
                                    {__('routes.validation.delivery_date_warning')}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tryb planowania — FIRST */}
                    <div className="form-group row mb-1">
                        <label className="col-lg-2 col-form-label">
                            Tryb planowania
                        </label>
                        <div className="col-lg-10">
                            <div className="btn-group" role="group" aria-label="Tryb planowania">
                                <Button
                                    className={cx('waves-effect waves-light', {
                                        'btn-primary': planningMode === 'departure',
                                        'btn-outline-secondary': planningMode !== 'departure',
                                    })}
                                    onClick={() => onPlanningModeChange('departure')}
                                >
                                    Planuj od wyjazdu
                                </Button>
                                <Button
                                    className={cx('waves-effect waves-light', {
                                        'btn-primary': planningMode === 'first_pickup',
                                        'btn-outline-secondary': planningMode !== 'first_pickup',
                                    })}
                                    onClick={() => onPlanningModeChange('first_pickup')}
                                >
                                    Planuj od 1. dostawy
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Oba pola dat w jednym wierszu */}
                    <div className="form-group row mb-1">
                        <label
                            htmlFor="departureAt"
                            className={cx('col-lg-2 col-form-label', { 'text-muted': isDepartureDisabled })}
                        >
                            {__('routes.departure_date_time')}
                        </label>
                        <div className="col-lg-4" style={isDepartureDisabled ? disabledDateStyle : undefined}>
                            <DatePicker
                                className={cx('form-control', {
                                    'is-invalid': (!!formik.errors.departureAt && formik.touched.departureAt)
                                        || (!!formik.errors.routeDate && formik.touched.routeDate),
                                })}
                                wrapperClassName="col-12"
                                selected={toDateOrNull(formik.values.departureAt)}
                                onChange={(date) => {
                                    if (date && !isValid(date)) return;
                                    formik.setFieldValue('departureAt', date ? format(date, 'yyyy-MM-dd HH:mm') : '');
                                    if (date) {
                                        formik.setFieldValue('routeDate', format(date, 'yyyy-MM-dd'));
                                        formik.setFieldValue('startTime', format(date, 'HH:mm'));

                                        if (travelToFirstPointMs !== null && travelToFirstPointMs !== undefined) {
                                            const firstPickup = new Date(date.getTime() + travelToFirstPointMs);
                                            formik.setFieldValue('firstPickupAt', format(firstPickup, 'yyyy-MM-dd HH:mm'));
                                        }
                                    } else {
                                        formik.setFieldValue('routeDate', '');
                                        formik.setFieldValue('startTime', '');
                                        formik.setFieldValue('firstPickupAt', '');
                                    }
                                }}
                                locale="pl"
                                showTimeSelect
                                dateFormat="yyyy-MM-dd HH:mm"
                                placeholderText={__('routes.departure_date_time_placeholder')}
                                isClearable
                                disabled={isDepartureDisabled}
                            />
                        </div>

                        <label
                            htmlFor="firstPickupAt"
                            className={cx('col-lg-2 col-form-label', { 'text-muted': isFirstPickupDisabled })}
                        >
                            {__('routes.first_pickup_date_time')}
                        </label>
                        <div className="col-lg-4" style={isFirstPickupDisabled ? disabledDateStyle : undefined}>
                            <DatePicker
                                className="form-control"
                                wrapperClassName="col-12"
                                selected={toDateOrNull(formik.values.firstPickupAt)}
                                onChange={(date) => {
                                    if (date && !isValid(date)) return;
                                    formik.setFieldValue('firstPickupAt', date ? format(date, 'yyyy-MM-dd HH:mm') : '');

                                    if (date) {
                                        // Always update departure fields so the timeline recalculation
                                        // effect (which depends on routeDate/startTime) fires. If the
                                        // travel time to the first point is already known we use it,
                                        // otherwise we seed departure = firstPickup and renderTimeLine
                                        // will correct it once directions are available.
                                        const newDeparture = (travelToFirstPointMs !== null && travelToFirstPointMs !== undefined)
                                            ? new Date(date.getTime() - travelToFirstPointMs)
                                            : date;
                                        formik.setFieldValue('departureAt', format(newDeparture, 'yyyy-MM-dd HH:mm'));
                                        formik.setFieldValue('routeDate', format(newDeparture, 'yyyy-MM-dd'));
                                        formik.setFieldValue('startTime', format(newDeparture, 'HH:mm'));
                                    } else {
                                        formik.setFieldValue('departureAt', '');
                                        formik.setFieldValue('routeDate', '');
                                        formik.setFieldValue('startTime', '');
                                    }
                                }}
                                locale="pl"
                                showTimeSelect
                                dateFormat="yyyy-MM-dd HH:mm"
                                placeholderText={__('routes.first_pickup_date_time_placeholder')}
                                isClearable
                                disabled={isFirstPickupDisabled}
                            />
                        </div>
                    </div>

                    <div>
                        {!!formik.errors.departureAt && formik.touched.departureAt && (
                            <div className="invalid-feedback d-block">{formik.errors.departureAt}</div>
                        )}
                        {!!formik.errors.routeDate && formik.touched.routeDate && (
                            <div className="invalid-feedback d-block">{formik.errors.routeDate}</div>
                        )}
                    </div>

                    <div className="form-group row mb-0">
                        <label htmlFor="defaultSpeed" className="col-lg-2 col-form-label">
                            {__('routes.unloading_time')}
                        </label>
                        <div className="col-lg-2">
                            <input
                                className="form-control"
                                id="defaultSpeed"
                                name="defaultSpeed"
                                type="text"
                                defaultValue={defaultUnloadingTime}
                                onChange={({ target: { value } }) => setDefaultUnloadingTime(value)}
                            />
                        </div>
                        <div className="col-lg-8">
                            <Button
                                className="btn-primary waves-effect waves-light"
                                onClick={() => handleUnloadingTimeForAllChange(defaultUnloadingTime)}
                            >
                                {__('routes.set_all_break_times')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 3: Speed */}
            <div className="form-group row">
                <label htmlFor="speed" className="col-lg-2 col-form-label">
                    {__('routes.speed_factor')}
                </label>
                <div className="col-lg-10">
                    <div className="input-group">
                        <input
                            className={cx('form-control', {
                                'is-invalid': formik.errors.speed && formik.touched.speed,
                            })}
                            id="speed"
                            name="speed"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.speed ?? ''}
                        />
                        <div className="input-group-append" id="route-speed-recalculate">
                            <Button
                                className="btn-primary waves-effect waves-light"
                                onClick={handleRecalculateSpeed}
                                icon="bx bx-sync"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 4: Vehicle + Driver */}
            <div className="form-group row">
                <label htmlFor="carId" className="col-lg-2 col-form-label">
                    {__('routes.vehicle_registration_number')}
                </label>
                <div className="col-lg-4">
                    <Select
                        options={carOptions}
                        components={{ IndicatorSeparator: null }}
                        styles={rsStyles(carInvalid)}
                        value={selectedCar}
                        placeholder={__('routes.car_plate_placeholder')}
                        isClearable
                        onChange={(opt) => formik.setFieldValue('carId', opt?.value ?? '')}
                        onBlur={() => formik.setFieldTouched('carId', true)}
                    />
                </div>

                <label htmlFor="driverId" className="col-lg-2 col-form-label">
                    {__('routes.driver')}
                </label>
                <div className="col-lg-4">
                    <Select
                        options={driverOptions}
                        components={{ IndicatorSeparator: null }}
                        styles={rsStyles(driverInvalid)}
                        value={selectedDriver}
                        placeholder={__('routes.driver_placeholder')}
                        isClearable
                        onChange={(opt) => formik.setFieldValue('driverId', opt?.value ?? '')}
                        onBlur={() => formik.setFieldTouched('driverId', true)}
                    />
                </div>
            </div>

            {/* Row 5: Ramp */}
            <div className="form-group row">
                <label htmlFor="rampNumber" className="col-lg-2 col-form-label">
                    {__('routes.ramp_number')}
                </label>
                <div className="col-lg-4">
                    <select
                        className={cx('form-control', {
                            'is-invalid': formik.errors.carId,
                        })}
                        id="rampNumber"
                        name="rampNumber"
                        type="select"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                            value={formik.values.rampNumber ?? ''}
                    >
                        <option>
                            Wybierz rampę
                        </option>

                        {ramps.map((ramp) => (
                            <option key={ramp}>
                                {ramp}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
};
